// ---------------------------------------------------------------------------
// Canvas Slice — App, Pages, Blocks CRUD using CogoBlockDef (runtime format)
//
// The editor operates directly on the same data model as cogo-react-renderer.
// No separate "editor format" — CogoElement trees are edited in-place.
// ---------------------------------------------------------------------------

import type {
  CogoAppDef,
  CogoPageDef,
  CogoBlockDef,
  CogoElement,
  CogoEventBinding,
  CogoFlow,
  LoadedApp,
  DesignTokens,
} from './types'
import type { AbsoluteBlockPosition } from '@cogo/types-v2'
import { isAbsolutePosition } from '@cogo/types-v2'
import { current } from 'immer'
import { canAddChild } from '@/features/page-editor/lib/bounded-flex'

// ---------------------------------------------------------------------------
// Slice interface
// ---------------------------------------------------------------------------

export interface CanvasSlice {
  // State — matches LoadedApp structure
  app: CogoAppDef | null
  blocks: Record<string, CogoBlockDef>
  data: Record<string, unknown>
  flows: Record<string, CogoFlow>
  tokens: DesignTokens | null
  activePageId: string | null

  // App actions
  loadApp: (loaded: LoadedApp) => void

  // Page actions
  setActivePage: (pageId: string) => void
  getActivePage: () => CogoPageDef | undefined
  getActivePageBlocks: () => CogoBlockDef[]

  // Page management actions
  addPage: (pageId: string) => void
  removePage: (pageId: string) => void
  renamePage: (oldId: string, newId: string) => void
  duplicatePage: (pageId: string) => void

  // Block actions (add/remove block name from page.blocks)
  createNewBlock: (pageId: string) => void
  addBlockToPage: (pageId: string, blockName: string) => void
  addImportedBlock: (blockName: string, blockDef: CogoBlockDef) => void
  removeBlockFromPage: (pageId: string, blockName: string) => void
  reorderPageBlocks: (pageId: string, fromIndex: number, toIndex: number) => void

  // Page meta actions
  updatePageMeta: (
    pageId: string,
    updates: { title?: string; description?: string; route?: string },
  ) => void
  setDefaultPage: (pageId: string) => void

  // Absolute layout actions (Phase 304)
  updateBlockPosition: (pageId: string, blockName: string, pos: Partial<AbsoluteBlockPosition>) => void
  setPageLayout: (pageId: string, layout: 'absolute' | 'flow') => void

  // Element tree actions (modify CogoElement within a block)
  updateElement: (blockName: string, elementId: string, updates: Partial<CogoElement>) => void
  removeElement: (blockName: string, elementId: string) => void
  removeElements: (blockName: string, elementIds: string[]) => void
  addChildElement: (blockName: string, parentId: string, element: CogoElement, index?: number) => void
  moveElement: (blockName: string, elementId: string, direction: 'up' | 'down') => void
  wrapElement: (blockName: string, elementId: string) => void
  renameElement: (blockName: string, oldId: string, newId: string) => void

  // Event binding actions
  setElementEvent: (blockName: string, elementId: string, eventType: string, ref: CogoEventBinding) => void
  removeElementEvent: (blockName: string, elementId: string, eventType: string) => void
}
// ---------------------------------------------------------------------------
// Helper: find element in a CogoElement tree (recursive)
// ---------------------------------------------------------------------------

export function findElementInTree(
  root: CogoElement,
  elementId: string,
): CogoElement | undefined {
  if (root.id === elementId) return root
  if (root.children) {
    for (const child of root.children) {
      const found = findElementInTree(child, elementId)
      if (found) return found
    }
  }
  return undefined
}

/** Find parent of an element in the tree */
export function findParentInTree(
  root: CogoElement,
  elementId: string,
): { parent: CogoElement; index: number } | undefined {
  if (root.children) {
    for (let i = 0; i < root.children.length; i++) {
      if (root.children[i].id === elementId) {
        return { parent: root, index: i }
      }
      const found = findParentInTree(root.children[i], elementId)
      if (found) return found
    }
  }
  return undefined
}

/** Collect all element IDs in a tree */
export function collectElementIds(root: CogoElement): string[] {
  const ids: string[] = [root.id]
  if (root.children) {
    for (const child of root.children) {
      ids.push(...collectElementIds(child))
    }
  }
  return ids
}

/** Get the ancestor path from root to the target element (inclusive) */
export function getAncestorPath(
  root: CogoElement,
  elementId: string,
): CogoElement[] {
  if (root.id === elementId) return [root]
  if (root.children) {
    for (const child of root.children) {
      const path = getAncestorPath(child, elementId)
      if (path.length > 0) return [root, ...path]
    }
  }
  return []
}

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

type Set = (fn: (state: CanvasSlice) => void) => void
type Get = () => CanvasSlice

function queueEditorBlockUiSave(blockName: string, block: CogoBlockDef) {
  const rootSnapshot = JSON.parse(JSON.stringify(block.ui.root)) as CogoElement
  const blockRecord = block as unknown as Record<string, unknown>
  const uiRecord = block.ui as unknown as Record<string, unknown>
  const metaRecord = block.meta as unknown as Record<string, unknown> | undefined
  const payload = {
    schemaVersion: blockRecord.schemaVersion ?? uiRecord.schemaVersion ?? '3.0',
    blockType: blockRecord.blockType ?? metaRecord?.type ?? metaRecord?.name ?? blockName,
    root: rootSnapshot,
  }

  queueMicrotask(() => {
    void Promise.all([
      import('@/store/studio-store'),
      import('@/store/auto-save'),
    ])
      .then(([{ useStudioStore }, { queueSave }]) => {
        const studio = useStudioStore.getState()
        const dbId = studio.blockIdMap?.[blockName]
        if (studio.projectSource !== 'server' || !studio.currentProjectId || !dbId) return
        studio.markDirty?.()
        queueSave({
          type: 'block',
          blockId: dbId,
          field: 'ui',
          data: payload,
        })
      })
      .catch((error) => {
        console.warn('[canvas-slice] Unable to queue block UI save', error)
      })
  })
}

export function createCanvasSlice(set: Set, get: Get, _api: unknown): CanvasSlice {

  return {
    // -- State --
    app: null,
    blocks: {},
    data: {},
    flows: {},
    tokens: null,
    activePageId: null,

    // -- App actions --
    loadApp: (loaded) =>
      set((state) => {
        state.app = loaded.manifest as CogoAppDef
        state.blocks = (loaded.blocks ?? {}) as Record<string, CogoBlockDef>
        state.data = loaded.data ?? {}
        state.flows = (loaded.flows ?? {}) as Record<string, CogoFlow>
        state.tokens = (loaded.tokens as DesignTokens) ?? null
        // Set first page as active
        if (loaded.manifest.pages.length > 0) {
          state.activePageId = loaded.manifest.pages[0].id
        }
      }),

    // -- Page actions --
    setActivePage: (pageId) =>
      set((state) => {
        state.activePageId = pageId
      }),

    getActivePage: () => {
      const { app, activePageId } = get()
      if (!app || !activePageId) return undefined
      return app.pages.find((p) => p.id === activePageId)
    },

    getActivePageBlocks: () => {
      const { app, activePageId, blocks } = get()
      if (!app || !activePageId) return []
      const page = app.pages.find((p) => p.id === activePageId)
      if (!page) return []
      return page.blocks
        .map((name) => blocks[name])
        .filter((b): b is CogoBlockDef => b != null)
    },

    // -- Page management --
    addPage: (pageId) =>
      set((state) => {
        if (!state.app) return
        state.app.pages.push({
          id: pageId,
          route: `/${pageId}`,
          blocks: [],
          layout: 'flow',
          blockLayout: {},
        })
        state.activePageId = pageId
      }),

    removePage: (pageId) =>
      set((state) => {
        if (!state.app) return
        // Don't allow removing the last page
        if (state.app.pages.length <= 1) return
        const removedPage = state.app.pages.find((page) => page.id === pageId)
        state.app.pages = state.app.pages.filter((p) => p.id !== pageId)
        const nextPage = state.app.pages[0]
        const navigation = state.app.navigation
        // If the removed page was active, switch to the first remaining page
        if (state.activePageId === pageId) {
          state.activePageId = nextPage?.id ?? null
        }
        if (navigation && navigation.defaultRoute === removedPage?.route && nextPage) {
          navigation.defaultRoute = nextPage.route
        }
      }),

    renamePage: (oldId, newId) =>
      set((state) => {
        if (!state.app) return
        const trimmed = newId.trim()
        if (!trimmed || trimmed === oldId) return
        // Prevent duplicate page IDs
        if (state.app.pages.some((p) => p.id === trimmed)) return
        const page = state.app.pages.find((p) => p.id === oldId)
        if (!page) return
        const previousRoute = page.route
        page.id = trimmed
        if (previousRoute === `/${oldId}`) {
          page.route = `/${trimmed}`
          if (state.app.navigation?.defaultRoute === previousRoute) {
            state.app.navigation.defaultRoute = page.route
          }
        }
        if (state.activePageId === oldId) {
          state.activePageId = trimmed
        }
      }),

    updatePageMeta: (pageId, updates) =>
      set((state) => {
        if (!state.app) return
        const page = state.app.pages.find((p) => p.id === pageId)
        if (!page) return
        const previousRoute = page.route
        if (updates.title !== undefined) (page as any).title = updates.title
        if (updates.description !== undefined) (page as any).description = updates.description
        if (updates.route !== undefined) {
          const trimmed = updates.route.trim()
          if (trimmed) {
            page.route = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
            if (state.app.navigation?.defaultRoute === previousRoute) {
              state.app.navigation.defaultRoute = page.route
            }
          }
        }
      }),

    setDefaultPage: (pageId) =>
      set((state) => {
        if (!state.app) return
        const page = state.app.pages.find((candidate) => candidate.id === pageId)
        if (!page) return
        state.app.navigation = {
          ...(state.app.navigation ?? {}),
          defaultRoute: page.route,
        }
      }),

    // -- Absolute layout actions (Phase 304) --
    updateBlockPosition: (pageId, blockName, pos) =>
      set((state) => {
        if (!state.app) return
        const page = state.app.pages.find((p) => p.id === pageId)
        if (!page || !page.blockPositions) return
        const current = page.blockPositions[blockName]
        if (current && isAbsolutePosition(current)) {
          Object.assign(current, pos)
        }
      }),

    setPageLayout: (pageId, layout) =>
      set((state) => {
        if (!state.app) return
        const page = state.app.pages.find((p) => p.id === pageId)
        if (!page) return
        page.layout = layout
        // Initialize blockPositions if switching to absolute and none exist
        if (layout === 'absolute' && !page.blockPositions) {
          page.blockPositions = {}
          page.blocks.forEach((name, i) => {
            page.blockPositions![name] = { x: 50, y: 50 + i * 200, w: 400, h: 150 }
          })
          if (!page.canvas) page.canvas = { w: 1920, h: 1080 }
        }
      }),

    duplicatePage: (pageId) =>
      set((state) => {
        if (!state.app) return
        const sourcePage = state.app.pages.find((p) => p.id === pageId)
        if (!sourcePage) return
        // Sequential copy naming: "tasks-copy", "tasks-copy-2", etc.
        const existingIds = new Set(state.app.pages.map((p) => p.id))
        let newId = `${pageId}-copy`
        if (existingIds.has(newId)) {
          let idx = 2
          while (existingIds.has(`${pageId}-copy-${idx}`)) idx++
          newId = `${pageId}-copy-${idx}`
        }
        // Deep-clone each block with new names so pages are independent
        const newBlockNames: string[] = []
        const duplicatedBlockNames = new Map<string, string>()
        for (const blockName of sourcePage.blocks) {
          const sourceBlock = state.blocks[blockName]
          if (!sourceBlock) continue
          const newBlockName = `${blockName}-${crypto.randomUUID().slice(0, 8)}`
          state.blocks[newBlockName] = structuredClone(current(sourceBlock))
          if (state.blocks[newBlockName].meta) {
            state.blocks[newBlockName].meta.name = newBlockName
          }
          newBlockNames.push(newBlockName)
          duplicatedBlockNames.set(blockName, newBlockName)
        }
        const duplicate = structuredClone(current(sourcePage))
        duplicate.id = newId
        duplicate.route = `/${newId}`
        duplicate.blocks = newBlockNames
        duplicate.blockLayout = { ...sourcePage.blockLayout }
        if (duplicate.blockPositions) {
          const sourcePositions = duplicate.blockPositions
          const renamedPositions: NonNullable<typeof duplicate.blockPositions> = {}
          sourcePage.blocks.forEach((blockName) => {
            const nextBlockName = duplicatedBlockNames.get(blockName)
            const position = sourcePositions[blockName]
            if (nextBlockName && position) renamedPositions[nextBlockName] = position
          })
          duplicate.blockPositions = renamedPositions
        }
        state.app.pages.push(duplicate)
        state.activePageId = newId
      }),

    // -- Block actions --
    createNewBlock: (pageId) =>
      set((state) => {
        if (!state.app) return
        const page = state.app.pages.find((p) => p.id === pageId)
        if (!page) return
        const blockName = `block-${crypto.randomUUID().slice(0, 8)}`
        const rootId = `el-${crypto.randomUUID().slice(0, 8)}`
        const kickerId = `el-${crypto.randomUUID().slice(0, 8)}`
        const titleId = `el-${crypto.randomUUID().slice(0, 8)}`
        const copyId = `el-${crypto.randomUUID().slice(0, 8)}`
        const actionsId = `el-${crypto.randomUUID().slice(0, 8)}`
        const primaryId = `el-${crypto.randomUUID().slice(0, 8)}`
        const statusId = `el-${crypto.randomUUID().slice(0, 8)}`
        state.blocks[blockName] = {
          meta: {
            name: blockName,
            type: 'ui',
            version: '1.0',
            description: 'Starter section created from the Page editor Add UI action.',
            stateContract: { localState: {}, inputSymbols: [], outputSymbols: [] },
          },
          ui: {
            root: {
              id: rootId,
              type: 'container',
              className: 'mx-auto flex w-full max-w-3xl flex-col gap-4 rounded-lg border border-border bg-card p-6 text-card-foreground shadow-sm',
              children: [
                {
                  id: kickerId,
                  type: 'text',
                  text: 'New Coogo surface',
                  className: 'text-xs font-semibold uppercase text-muted-foreground',
                },
                {
                  id: titleId,
                  type: 'text',
                  text: 'Build the first useful screen',
                  tag: 'h2',
                  className: 'text-2xl font-semibold tracking-normal',
                },
                {
                  id: copyId,
                  type: 'text',
                  text: 'Replace this starter content with generated UI, data, and ActionFlow behavior before publishing.',
                  tag: 'p',
                  className: 'max-w-2xl text-sm text-muted-foreground',
                },
                {
                  id: actionsId,
                  type: 'container',
                  className: 'flex flex-wrap items-center gap-2 pt-2',
                  children: [
                    {
                      id: primaryId,
                      type: 'button',
                      text: 'Add interaction',
                      variant: 'default',
                      className: 'min-w-32',
                    },
                    {
                      id: statusId,
                      type: 'text',
                      text: 'Ready for variables, datasource, and flow wiring',
                      className: 'text-xs text-muted-foreground',
                    },
                  ],
                  layout: { type: 'flex', direction: 'row', gap: '2' },
                },
              ],
              layout: { type: 'flex', direction: 'column', gap: '4' },
            },
          },
          events: {},
          flow: {},
          state: {},
          data: {},
        }
        page.blocks.push(blockName)
      }),

    addImportedBlock: (blockName, blockDef) =>
      set((state) => {
        state.blocks[blockName] = blockDef
      }),

    addBlockToPage: (pageId, blockName) =>
      set((state) => {
        if (!state.app) return
        const page = state.app.pages.find((p) => p.id === pageId)
        if (page && !page.blocks.includes(blockName)) {
          page.blocks.push(blockName)
        }
      }),

    removeBlockFromPage: (pageId, blockName) =>
      set((state) => {
        if (!state.app) return
        const page = state.app.pages.find((p) => p.id === pageId)
        if (page) {
          page.blocks = page.blocks.filter((n) => n !== blockName)
        }
        // Clean up orphaned block definition if no page references it
        const isReferenced = state.app.pages.some((p) => p.blocks.includes(blockName))
        if (!isReferenced) {
          delete state.blocks[blockName]
        }
      }),

    reorderPageBlocks: (pageId, fromIndex, toIndex) =>
      set((state) => {
        if (!state.app) return
        const page = state.app.pages.find((p) => p.id === pageId)
        if (!page) return
        const [name] = page.blocks.splice(fromIndex, 1)
        if (name) page.blocks.splice(toIndex, 0, name)
      }),

    // -- Element tree actions --
    updateElement: (blockName, elementId, updates) =>
      set((state) => {
        const block = state.blocks[blockName]
        if (!block) return
        const el = findElementInTree(block.ui.root, elementId)
        if (el) {
          Object.assign(el, updates)
          queueEditorBlockUiSave(blockName, block)
        }
      }),

    removeElement: (blockName, elementId) =>
      set((state) => {
        const block = state.blocks[blockName]
        if (!block) return
        const result = findParentInTree(block.ui.root, elementId)
        if (result && result.parent.children) {
          result.parent.children.splice(result.index, 1)
          queueEditorBlockUiSave(blockName, block)
        }
      }),

    removeElements: (blockName, elementIds) =>
      set((state) => {
        const block = state.blocks[blockName]
        if (!block) return
        let changed = false
        // Remove in reverse tree-order to avoid index shifts for siblings
        for (const id of elementIds) {
          const result = findParentInTree(block.ui.root, id)
          if (result && result.parent.children) {
            result.parent.children.splice(result.index, 1)
            changed = true
          }
        }
        if (changed) queueEditorBlockUiSave(blockName, block)
      }),

    addChildElement: (blockName, parentId, element, index) =>
      set((state) => {
        const block = state.blocks[blockName]
        if (!block) return
        const parent = findElementInTree(block.ui.root, parentId)
        if (parent) {
          // maxChildren enforcement
          const currentCount = parent.children?.length ?? 0
          if (!canAddChild(parent.type, currentCount)) return
          if (!parent.children) parent.children = []
          if (index != null) {
            parent.children.splice(index, 0, element)
          } else {
            parent.children.push(element)
          }
          queueEditorBlockUiSave(blockName, block)
        }
      }),

    moveElement: (blockName, elementId, direction) =>
      set((state) => {
        const block = state.blocks[blockName]
        if (!block) return
        const result = findParentInTree(block.ui.root, elementId)
        if (!result) return
        const siblings = result.parent.children
        if (!siblings) return
        const idx = result.index
        if (direction === 'up' && idx > 0) {
          // Swap with the sibling above
          const temp = siblings[idx - 1]
          siblings[idx - 1] = siblings[idx]
          siblings[idx] = temp
          queueEditorBlockUiSave(blockName, block)
        } else if (direction === 'down' && idx < siblings.length - 1) {
          // Swap with the sibling below
          const temp = siblings[idx + 1]
          siblings[idx + 1] = siblings[idx]
          siblings[idx] = temp
          queueEditorBlockUiSave(blockName, block)
        }
      }),

    wrapElement: (blockName, elementId) =>
      set((state) => {
        const block = state.blocks[blockName]
        if (!block) return
        const result = findParentInTree(block.ui.root, elementId)
        if (!result) return
        const children = result.parent.children
        if (!children) return
        const idx = result.index
        // Remove the element from its current position
        const [element] = children.splice(idx, 1)
        // Create a new container wrapping the element
        const wrapperId = `el-${crypto.randomUUID().slice(0, 8)}`
        const wrapper: CogoElement = {
          id: wrapperId,
          type: 'container',
          className: 'flex flex-col gap-2 p-2',
          children: [element],
          layout: { type: 'flex', direction: 'column', gap: '2' },
        }
        // Insert the wrapper in the same position
        children.splice(idx, 0, wrapper)
        queueEditorBlockUiSave(blockName, block)
      }),

    renameElement: (blockName, oldId, newId) =>
      set((state) => {
        const block = state.blocks[blockName]
        if (!block) return
        const trimmed = newId.trim()
        if (!trimmed || trimmed === oldId) return
        // Prevent duplicate element IDs
        if (findElementInTree(block.ui.root, trimmed)) return
        const el = findElementInTree(block.ui.root, oldId)
        if (!el) return
        el.id = trimmed
        queueEditorBlockUiSave(blockName, block)
      }),

    // -- Event binding actions --
    setElementEvent: (blockName, elementId, eventType, ref) =>
      set((state) => {
        const block = state.blocks[blockName]
        if (!block) return
        const el = findElementInTree(block.ui.root, elementId)
        if (!el) return
        if (!el.events) el.events = {}
        el.events[eventType] = ref
        queueEditorBlockUiSave(blockName, block)
      }),

    removeElementEvent: (blockName, elementId, eventType) =>
      set((state) => {
        const block = state.blocks[blockName]
        if (!block) return
        const el = findElementInTree(block.ui.root, elementId)
        if (!el || !el.events) return
        delete el.events[eventType]
        // Clean up empty events object
        if (Object.keys(el.events).length === 0) {
          delete el.events
        }
        queueEditorBlockUiSave(blockName, block)
      }),
  }
}
